import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import type { MediaAssetResponse } from 'we-tree-api-client/v2';
import { mediaApi, displayStatusFromMediaAsset } from '@/api/mediaWrapper';
import { WeTreeApiError } from '@/api/errors';
import { useDomainStore } from '@/hooks/useStores';
import { getApiConfiguration } from '@/services/SessionService';
import { familyHasPaidPlan, getFamilyPlanUpgradeHint, isFamilyOwner } from '@/utils/familyPlan';
import { getMediaFileCategoryFromMimeType, type MediaFileCategory } from '@/utils/mediaUpload';

import './image-enlarge-overlay.css';

export interface ImageEnlargeOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  familyId: string;
  /** For profile photo: pass familyMemberId. */
  familyMemberId?: string;
  /** For citation/any media: pass mediaAssetId. Use exactly one of familyMemberId or mediaAssetId when open. */
  mediaAssetId?: string;
  /** Optional MIME hint to skip an extra asset fetch (still refreshed from API when missing). */
  mimeType?: string | null;
  /**
   * When the viewer may manage access on this asset (`canManageAccess`), show a Permissions action
   * that calls this with the loaded asset. Hosts (media library, memory attachments) open
   * ManageContentPermissionsModal. Omit for overlays that should not offer permissions (e.g. profile photo).
   */
  onManagePermissions?: (asset: MediaAssetResponse) => void;
}

async function resolveAbsoluteUrl(url: string): Promise<string> {
  if (url.startsWith('http')) return url;
  const config = await getApiConfiguration();
  if (config?.basePath) {
    const origin = config.basePath.replace(/\/v2\/?$/, '');
    return `${origin}${url}`;
  }
  return url;
}

const ImageEnlargeOverlay: React.FC<ImageEnlargeOverlayProps> = observer(
  ({ isOpen, onClose, familyId, familyMemberId, mediaAssetId, mimeType: mimeTypeProp, onManagePermissions }) => {
    const { t } = useTranslation('screens');
    const { t: tCommon } = useTranslation('common');
    const domainStore = useDomainStore();
    const planKnownForFamily = domainStore.selectedFamilyId === familyId;
    const familyPlanPaid = planKnownForFamily && familyHasPaidPlan(domainStore.currentFamily?.plan);
    const upgradeHint = getFamilyPlanUpgradeHint(
      planKnownForFamily && isFamilyOwner(domainStore.currentFamily?.userRole)
    );

    const [mediaUrl, setMediaUrl] = useState<string | null>(null);
    const [posterUrl, setPosterUrl] = useState<string | null>(null);
    const [category, setCategory] = useState<MediaFileCategory>('photo');
    const [fileName, setFileName] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [upgradeRequired, setUpgradeRequired] = useState(false);
    /** Access fields from getMediaAsset — used to gate the Permissions entry point. */
    const [loadedAsset, setLoadedAsset] = useState<MediaAssetResponse | null>(null);
    const refreshAttemptedRef = useRef(false);

    const handleOpenChange = useCallback(
      (open: boolean) => {
        if (!open) onClose();
      },
      [onClose]
    );

    const loadMedia = useCallback(
      async (options?: { forceOriginal?: boolean }) => {
        if (!familyId) return;

        setIsLoading(true);
        setError(null);
        setUpgradeRequired(false);

        if (mediaAssetId) {
          let resolvedCategory = getMediaFileCategoryFromMimeType(mimeTypeProp) ?? null;
          let resolvedName: string | null = null;
          let assetData: MediaAssetResponse | undefined;

          const assetResult = await mediaApi.getMediaAsset(familyId, mediaAssetId);
          if (assetResult.success) {
            assetData = assetResult.data;
            setLoadedAsset(assetResult.data);
            resolvedCategory =
              getMediaFileCategoryFromMimeType(assetResult.data.mimeType) ?? resolvedCategory ?? 'photo';
            resolvedName = assetResult.data.originalFilename ?? null;
          } else {
            setLoadedAsset(null);
          }

          const kind = resolvedCategory ?? 'photo';
          setCategory(kind);
          setFileName(resolvedName);

          if (kind === 'video' && assetData) {
            const derived = displayStatusFromMediaAsset(assetData);
            if (assetData.status === 'processing' || assetData.status === 'uploading') {
              setIsLoading(false);
              setMediaUrl(null);
              setError(derived.message || t('mediaViewer.processingVideo'));
              return;
            }
            if (assetData.status === 'failed') {
              setIsLoading(false);
              setMediaUrl(null);
              setError(derived.message || t('mediaViewer.videoFailed'));
              return;
            }
          }

          const isPaidOnlyMedia = kind === 'video' || kind === 'audio';
          let nextPoster: string | null = null;
          if (kind === 'video') {
            nextPoster = await mediaApi.getVideoPosterUrl(familyId, mediaAssetId);
          }
          setPosterUrl(nextPoster);

          // Soft gate: skip source URL fetch on free so we show Upgrade to play without a 403.
          if (isPaidOnlyMedia && planKnownForFamily && !familyPlanPaid) {
            setIsLoading(false);
            setMediaUrl(null);
            setUpgradeRequired(true);
            setError(kind === 'video' ? t('mediaViewer.upgradeToPlayVideo') : t('mediaViewer.upgradeToPlayAudio'));
            return;
          }

          const size = kind === 'photo' && !options?.forceOriginal ? '800x800' : 'original';
          const result = await mediaApi.getDownloadUrl(familyId, mediaAssetId, size);
          setIsLoading(false);
          if (result.success && result.data?.url) {
            setMediaUrl(await resolveAbsoluteUrl(result.data.url));
            return;
          }

          setMediaUrl(null);
          if (result.success === false && result.error instanceof WeTreeApiError && result.error.isUpgradeRequired()) {
            setUpgradeRequired(true);
            setError(
              kind === 'video'
                ? t('mediaViewer.upgradeToPlayVideo')
                : kind === 'audio'
                  ? t('mediaViewer.upgradeToPlayAudio')
                  : result.error.getUserMessage()
            );
            return;
          }

          if (assetData && kind === 'video') {
            const derived = displayStatusFromMediaAsset(assetData);
            setError(
              derived.displayStatus === 'processing'
                ? derived.message || t('mediaViewer.processingVideo')
                : derived.message || t('mediaViewer.videoUnavailable')
            );
          } else {
            setError(kind === 'photo' ? t('mediaViewer.imageUnavailable') : t('mediaViewer.mediaUnavailable'));
          }
          return;
        }

        if (familyMemberId) {
          setCategory('photo');
          setFileName(null);
          const result = await mediaApi.getPrimaryProfilePhotoDisplay(familyId, familyMemberId, '800x800');
          setIsLoading(false);
          if (result.success && result.data) {
            if (result.data.url && result.data.displayStatus === 'safe') {
              setMediaUrl(result.data.url);
            } else {
              setMediaUrl(null);
              setError(
                result.data.displayStatus === 'processing'
                  ? t('mediaViewer.processing')
                  : t('mediaViewer.photoUnavailable')
              );
            }
          } else {
            setMediaUrl(null);
            setError(t('mediaViewer.photoUnavailable'));
          }
        }
      },
      [familyId, familyMemberId, mediaAssetId, mimeTypeProp, familyPlanPaid, planKnownForFamily, t]
    );

    useEffect(() => {
      if (!isOpen) {
        setMediaUrl(null);
        setPosterUrl(null);
        setError(null);
        setFileName(null);
        setCategory('photo');
        setUpgradeRequired(false);
        setLoadedAsset(null);
        refreshAttemptedRef.current = false;
        return;
      }

      let cancelled = false;
      refreshAttemptedRef.current = false;
      (async () => {
        await loadMedia();
        if (cancelled) return;
      })();
      return () => {
        cancelled = true;
      };
    }, [isOpen, loadMedia]);

    const canManagePermissions = Boolean(onManagePermissions) && loadedAsset?.canManageAccess === true;

    const handleMediaError = useCallback(async () => {
      if (!mediaAssetId || refreshAttemptedRef.current) {
        setError(category === 'photo' ? t('mediaViewer.imageUnavailable') : t('mediaViewer.playbackFailed'));
        return;
      }
      refreshAttemptedRef.current = true;
      await loadMedia({ forceOriginal: category !== 'photo' });
    }, [mediaAssetId, category, loadMedia, t]);

    const handleDownload = useCallback(() => {
      if (!mediaUrl || upgradeRequired) return;
      const a = document.createElement('a');
      a.href = mediaUrl;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      if (fileName) a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }, [mediaUrl, fileName, upgradeRequired]);

    const isMediaAsset = Boolean(mediaAssetId);
    const ariaLabel =
      upgradeRequired && (category === 'video' || category === 'audio')
        ? t('mediaViewer.upgradeToPlay')
        : category === 'video'
          ? t('mediaViewer.videoPlayer')
          : category === 'audio'
            ? t('mediaViewer.audioPlayer')
            : isMediaAsset
              ? t('mediaViewer.enlargedImage')
              : t('mediaViewer.enlargedProfilePhoto');
    const imgAlt = isMediaAsset ? t('mediaViewer.mediaAlt') : t('mediaViewer.profileAlt');

    return (
      <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay className="v01-modal-overlay image-enlarge-overlay" />
          <Dialog.Content className="image-enlarge-overlay__content" aria-label={ariaLabel}>
            <button
              type="button"
              className="v01-modal__close image-enlarge-overlay__close"
              onClick={onClose}
              aria-label={tCommon('close')}
            >
              ×
            </button>
            {isLoading && (
              <div className="image-enlarge-overlay__loading">
                <div className="v01-modal__spinner" />
                <span>{t('mediaViewer.loading')}</span>
              </div>
            )}
            {upgradeRequired && !isLoading && (
              <div className="image-enlarge-overlay__media-stack">
                <div className="image-enlarge-overlay__upgrade">
                  {posterUrl ? <img src={posterUrl} alt="" className="image-enlarge-overlay__upgrade-poster" /> : null}
                  <div className="image-enlarge-overlay__upgrade-body">
                    <p className="image-enlarge-overlay__upgrade-title">{error || t('mediaViewer.upgradeToPlay')}</p>
                    <p className="image-enlarge-overlay__upgrade-hint">{upgradeHint}</p>
                  </div>
                </div>
              </div>
            )}
            {error && !isLoading && !upgradeRequired && <div className="image-enlarge-overlay__error">{error}</div>}
            {mediaUrl && !isLoading && !upgradeRequired && (
              <div className="image-enlarge-overlay__media-stack">
                <div className="image-enlarge-overlay__image-wrap" data-category={category}>
                  {category === 'video' ? (
                    <video
                      key={mediaUrl}
                      className="image-enlarge-overlay__video"
                      src={mediaUrl}
                      poster={posterUrl ?? undefined}
                      controls
                      playsInline
                      preload="metadata"
                      onError={() => {
                        void handleMediaError();
                      }}
                    />
                  ) : category === 'audio' ? (
                    <div className="image-enlarge-overlay__audio-shell">
                      <audio
                        key={mediaUrl}
                        className="image-enlarge-overlay__audio"
                        src={mediaUrl}
                        controls
                        preload="metadata"
                        onError={() => {
                          void handleMediaError();
                        }}
                      />
                      {fileName && <p className="image-enlarge-overlay__audio-name">{fileName}</p>}
                    </div>
                  ) : (
                    <img
                      src={mediaUrl}
                      alt={imgAlt}
                      className="image-enlarge-overlay__image"
                      onError={() => {
                        void handleMediaError();
                      }}
                    />
                  )}
                  {mediaAssetId && (
                    <div className="image-enlarge-overlay__actions">
                      {canManagePermissions && loadedAsset && (
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => onManagePermissions?.(loadedAsset)}
                        >
                          {t('mediaViewer.permissions')}
                        </button>
                      )}
                      <button type="button" className="btn btn--ghost btn--sm" onClick={handleDownload}>
                        {t('mediaViewer.download')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }
);

export default ImageEnlargeOverlay;
